import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { warsawInstant, warsawParts } from '../events/labels';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { RescheduleDialog } from './RescheduleDialog';
import { instantText, isOverdue, SCHEDULE_KEY, splitTasks, TASK_KINDS, TASK_PRIORITIES, TASKS_RESOURCE, type Task, type TaskKind, type TaskPriority } from './types';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type PersonOption = { id: string; name: string };
type Contractor = { id: string; name: string; kind: 'client' | 'supplier' | 'both' };

export const tasksKey = (eventId: string) => [...SCHEDULE_KEY, 'tasks', eventId];

function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Nie udało się zapisać';
  return fieldIssues(error).map((i) => i.message).join(' ') || error.message;
}

/** One line under the title: due time, who, priority, supplier of a return, where it came from, the last reason. */
export function TaskMeta({ task, showEvent = false, showPerson = true }: { task: Task; showEvent?: boolean; showPerson?: boolean }) {
  const overdue = isOverdue(task);
  return (
    <span className="task-meta">
      {task.dueAt && <span className={overdue ? 'task-overdue' : undefined}>{overdue ? `po terminie: ${instantText(task.dueAt)}` : `do ${instantText(task.dueAt)}`}</span>}
      {showEvent && task.eventName && <span>{task.canOpen && task.eventId ? <Link to={`/wydarzenia?wydarzenie=${task.eventId}`}>{task.eventName}</Link> : task.eventName}</span>}
      {showPerson && task.personName && <span>{task.personName}</span>}
      {task.priority === 'pilny' && <span className="task-urgent">pilne</span>}
      {task.priority === 'niski' && <span>niski priorytet</span>}
      {task.kind === 'zwrot_wypozyczenia' && <span>zwrot: {task.contractorName ?? 'dostawca'}</span>}
      {task.fromFunction && <span>z listy funkcji</span>}
      {task.lastReason && (
        <span>
          przeniesione{task.rescheduleCount && task.rescheduleCount > 1 ? ` (${task.rescheduleCount}×)` : ''}: {task.lastReason}
        </span>
      )}
    </span>
  );
}

/**
 * "Zadania" in the "Harmonogram" tab: the event's tasks (from the functions of the crew and added by hand), open
 * first. Tick off, move with a reason, edit, delete; "+ Dodaj zadanie" also for a rental return to a supplier.
 */
export function TasksPanel({ eventId, crew }: { eventId: string; crew: PersonOption[] }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const key = tasksKey(eventId);
  const list = useQuery({ queryKey: key, enabled: !!me && can(me, TASKS_RESOURCE, 'view'), queryFn: () => api<{ tasks: Task[] }>(`/api/schedule/events/${eventId}/tasks`) });
  const [form, setForm] = useState<Task | 'new' | null>(null);
  const [moving, setMoving] = useState<Task | null>(null);
  const [ticking, setTicking] = useState<Record<string, boolean>>({});
  const refresh = () => queryClient.invalidateQueries({ queryKey: SCHEDULE_KEY });
  const tick = useMutation({
    mutationFn: (t: Task) => api<Task>(`/api/schedule/tasks/${t.id}/done`, { method: 'POST', body: { done: !t.done } }),
    onSettled: async (_d, _e, t) => {
      await refresh();
      setTicking(({ [t.id]: _, ...rest }) => rest);
    },
  });
  const remove = useMutation({ mutationFn: (t: Task) => api(`/api/schedule/tasks/${t.id}?version=${t.version}`, { method: 'DELETE' }), onSettled: refresh });

  if (!me || !can(me, TASKS_RESOURCE, 'view')) return null;
  const items = (list.data?.tasks ?? []).map((t) => (t.id in ticking ? { ...t, done: ticking[t.id]! } : t));
  const { open, done } = splitTasks(items);
  const mayTick = can(me, TASKS_RESOURCE, 'edit') && fieldVisible(me, TASKS_RESOURCE, 'done');
  const mayEdit = can(me, TASKS_RESOURCE, 'edit');
  const mayMove = mayEdit && fieldVisible(me, TASKS_RESOURCE, 'dueAt');
  const mayDelete = can(me, TASKS_RESOURCE, 'delete');
  const mayAdd = can(me, TASKS_RESOURCE, 'create') && fieldVisible(me, TASKS_RESOURCE, 'title') && fieldVisible(me, TASKS_RESOURCE, 'dueAt');
  const writeError = [tick.error, remove.error].find((e) => e instanceof ApiError) as ApiError | undefined;

  const row = (t: Task) => (
    <li key={t.id} className={`task-row${t.done ? ' done' : ''}`}>
      <label className="check">
        <input
          type="checkbox"
          checked={!!t.done}
          disabled={!mayTick || tick.isPending}
          aria-label={`Zrobione: ${t.title ?? 'zadanie'}`}
          onChange={() => {
            setTicking((s) => ({ ...s, [t.id]: !t.done }));
            tick.mutate(t);
          }}
        />
        <span className="task-main">
          <span className="task-title">{t.title ?? 'Zadanie'}</span>
          <TaskMeta task={t} />
        </span>
      </label>
      {(mayEdit || mayDelete) && (
        <RowMenu
          label={`Więcej działań: ${t.title ?? 'zadanie'}`}
          actions={[
            ...(mayMove && !t.done ? [{ label: 'Przenieś termin…', onSelect: () => setMoving(t) }] : []),
            ...(mayEdit ? [{ label: 'Edytuj', onSelect: () => leaveGuarded(() => setForm(t)) }] : []),
            ...(mayDelete ? [{ label: 'Usuń', danger: true, onSelect: () => remove.mutate(t) }] : []),
          ]}
        />
      )}
    </li>
  );

  return (
    <section className="schedule-section" aria-label="Zadania" data-feature-id="schedule.tasks">
      <div className="schedule-head">
        <h3>
          Zadania
          {open.length > 0 && <span className="count" aria-label={`do zrobienia: ${open.length}`}>{open.length}</span>}
        </h3>
        {mayAdd && form !== 'new' && (
          <button className="btn small ghost" type="button" onClick={() => leaveGuarded(() => setForm('new'))}>
            + Dodaj zadanie
          </button>
        )}
      </div>
      {list.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać zadań'}</p>}
      {writeError?.status === 409 && (
        <ConflictNotice
          onReload={() => {
            tick.reset();
            remove.reset();
            void refresh();
          }}
        />
      )}
      {writeError && writeError.status !== 409 && <p className="alert" role="alert">{writeError.message}</p>}
      {list.data && items.length === 0 && form !== 'new' && (
        <p className="muted small">Brak zadań. Zadania z list funkcji pojawią się same, gdy dodasz osoby do obsady; możesz też dodać własne.</p>
      )}
      {open.length > 0 && <ul className="task-list" aria-label="Zadania do zrobienia">{open.map(row)}</ul>}
      {done.length > 0 && (
        <details className="task-done">
          <summary>Zrobione ({done.length})</summary>
          <ul className="task-list" aria-label="Zadania zrobione">
            {done.map(row)}
          </ul>
        </details>
      )}
      {form !== null && <TaskForm me={me} eventId={eventId} base={form === 'new' ? null : form} crew={crew} onDone={() => setForm(null)} />}
      {moving && <RescheduleDialog task={moving} onClose={() => setMoving(null)} onDone={refresh} />}
    </section>
  );
}

/** Add or edit a task. The due time is set here only for a new task; later it moves with a reason ("Przenieś termin"). */
function TaskForm({ me, eventId, base, crew, onDone }: { me: Me; eventId: string; base: Task | null; crew: PersonOption[]; onDone: () => void }) {
  const queryClient = useQueryClient();
  const due = base?.dueAt ? warsawParts(base.dueAt) : { day: '', time: '09:00' };
  const [title, setTitle] = useState(base?.title ?? '');
  const [day, setDay] = useState(due.day);
  const [time, setTime] = useState(due.time);
  const [priority, setPriority] = useState<TaskPriority>(base?.priority ?? 'normalny');
  const [kind, setKind] = useState<TaskKind>(base?.kind ?? 'zwykle');
  const [personId, setPersonId] = useState(base?.personId ?? '');
  const [contractorId, setContractorId] = useState(base?.contractorId ?? '');
  const show = (field: string) => fieldVisible(me, TASKS_RESOURCE, field);
  const seeContractors = can(me, 'contractors.contractors', 'view');
  const contractors = useQuery({ queryKey: ['contractors', 'task-supplier'], enabled: seeContractors && kind === 'zwrot_wypozyczenia', queryFn: () => api<{ contractors: Contractor[] }>('/api/contractors') });
  const suppliers = (contractors.data?.contractors ?? []).filter((c) => c.kind !== 'client');
  // a person outside the crew (e.g. a task added for the owner) stays selectable when editing
  const people = base?.personId && !crew.some((p) => p.id === base.personId) ? [...crew, { id: base.personId, name: base.personName ?? 'Osoba' }] : crew;

  const save = useMutation({
    mutationFn: () => {
      const all: Record<string, unknown> = {
        title: title.trim(),
        priority,
        kind,
        personId: personId || null,
        contractorId: kind === 'zwrot_wypozyczenia' && contractorId ? contractorId : null,
        ...(base ? {} : { dueAt: warsawInstant(day, time) }),
      };
      // rule 7a: only visible fields, and when editing only the changed ones
      const body = Object.fromEntries(
        Object.entries(all).filter(([k, v]) => show(k) && (!base || (base as Record<string, unknown>)[k] !== v)),
      );
      return base ? api<Task>(`/api/schedule/tasks/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api<Task>(`/api/schedule/events/${eventId}/tasks`, { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SCHEDULE_KEY });
      onDone();
    },
  });
  const ready = title.trim() !== '' && (base !== null || (day !== '' && time !== '')) && (kind !== 'zwrot_wypozyczenia' || contractorId !== '' || !show('contractorId'));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const now = { title, day, time, priority, kind, personId, contractorId };
  const opening = useOpening(now);
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(now, opening),
    save: async () => {
      if (!ready) return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="schedule-form" aria-label={base ? `Edycja zadania: ${base.title ?? ''}` : 'Nowe zadanie'} onSubmit={submit}>
      <label className="field">
        <span>Co jest do zrobienia</span>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="np. Odebrać klucze do sali" maxLength={500} autoComplete="off" required />
      </label>
      <div className="field-row">
        {!base && (
          <>
            <label className="field">
              <span>Termin: dzień</span>
              <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
            </label>
            <label className="field">
              <span>Godzina</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
            </label>
          </>
        )}
        {show('priority') && (
          <label className="field">
            <span>Priorytet</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
              {(Object.keys(TASK_PRIORITIES) as TaskPriority[]).map((p) => (
                <option key={p} value={p}>
                  {TASK_PRIORITIES[p]}
                </option>
              ))}
            </select>
          </label>
        )}
        {show('personId') && (
          <label className="field">
            <span>Kto</span>
            <select value={personId} onChange={(e) => setPersonId(e.target.value)}>
              <option value="">— nikt —</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
        {show('kind') && (
          <label className="field">
            <span>Rodzaj</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as TaskKind)}>
              {(Object.keys(TASK_KINDS) as TaskKind[]).map((k) => (
                // a return needs a supplier: without seeing that field the role cannot add one
                <option key={k} value={k} disabled={k === 'zwrot_wypozyczenia' && !show('contractorId') && kind !== k}>
                  {TASK_KINDS[k]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {kind === 'zwrot_wypozyczenia' && show('contractorId') && (
        <label className="field" data-feature-id="schedule.returns">
          <span>Dostawca, któremu oddajemy sprzęt</span>
          {seeContractors ? (
            <select value={contractorId} onChange={(e) => setContractorId(e.target.value)} required>
              <option value="">— wybierz dostawcę —</option>
              {suppliers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              {base?.contractorId && !suppliers.some((c) => c.id === base.contractorId) && <option value={base.contractorId}>{base.contractorName ?? 'Dostawca'}</option>}
            </select>
          ) : (
            <span className="muted">Nie widzisz kontrahentów — dostawcę wybierze ktoś z prawem do kontrahentów.</span>
          )}
          {seeContractors && (
            <span className="hint field-hint">
              Nie ma go na liście? Dodaj go w <Link to="/kontrahenci">Kontrahentach</Link> (rodzaj „dostawca”).
            </span>
          )}
        </label>
      )}
      {show('personId') && personId === '' && <p className="hint">Zadanie bez osoby nie wysyła przypomnień po terminie.</p>}
      {crew.length === 0 && show('personId') && <p className="hint">Osoby wybierasz z obsady — dodaj je w zakładce „Obsada”.</p>}
      {save.error && (
        <p className="alert" role="alert">
          {errorText(save.error)}
        </p>
      )}
      <div className="actions">
        <button className="btn small primary" type="submit" disabled={!ready || save.isPending}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz' : 'Dodaj zadanie'}
        </button>
        <button className="btn small" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
